import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  ClockCircleOutlined,
  DollarOutlined,
  DownloadOutlined,
  FileTextOutlined,
  ProjectOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Button, DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import {
  useDropUnavailableCompany,
  useReportSummary,
  useTimeTrackingCompanies,
} from '@/app/time-tracking/_shared/hooks';
import {
  ContentBody,
  ContentHeader,
  ContentWrapper,
} from '@/components/content';
import { PageSizeDropdown } from '@/components/data-table/PageSizeDropdown';
import { PageHeader } from '@/components/page-header';
import { ROLES, PERMISSIONS } from '@/constants';
import useEnforceAccessControl from '@/hooks/useEnforceAccessControl';
import { cn } from '@/libs/utils';
import { useTimeTrackingStore } from '@/store/useTimeTrackingStore';
import ReportAnalytics from './_components/ReportAnalytics';
import ReportByClient from './_components/ReportByClient';
import ReportByEmployee from './_components/ReportByEmployee';
import ReportByProject from './_components/ReportByProject';
import ReportExportModal from './_components/ReportExportModal';

const { RangePicker } = DatePicker;

export default function ReportsPage() {
  const { t } = useTranslation('forms');
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdmin = useEnforceAccessControl({
    requiredRoles: [
      ROLES.superAdmin,
      ROLES.admin,
      ROLES.client,
      ROLES.clientOfficer,
      ROLES.hrOfficer,
    ],
    requiredPermissions: [PERMISSIONS.employeeView],
  });

  const { data: companyData, isLoading: companyIsLoading } =
    useTimeTrackingCompanies();
  const { selectedCompany, setSelectedCompany } = useTimeTrackingStore();

  // Read params from URL
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';
  const reportType = searchParams.get('reportType') || 'by-project';
  const offset = searchParams.get('offset') || '1';
  const limit = searchParams.get('limit') || '10';
  const company = searchParams.get('company') || '';

  const dropCompany = useCallback(() => {
    setSelectedCompany(null);
    const next = new URLSearchParams(searchParams);
    next.delete('company');
    setSearchParams(next, { replace: true });
  }, [setSelectedCompany, searchParams, setSearchParams]);

  useDropUnavailableCompany(companyData, company, dropCompany);

  // Auto-select: single company, or restore from store
  useEffect(() => {
    if (company) return;
    if (companyData?.isSingle) {
      const next = new URLSearchParams(searchParams);
      next.set('company', companyData.options[0].value);
      setSearchParams(next, { replace: true });
    } else if (selectedCompany) {
      const next = new URLSearchParams(searchParams);
      next.set('company', selectedCompany);
      setSearchParams(next, { replace: true });
    }
  }, [companyData, company, selectedCompany, searchParams, setSearchParams]);

  // Sync store when company changes
  useEffect(() => {
    if (company && company !== selectedCompany) {
      setSelectedCompany(company);
    }
  }, [company, selectedCompany, setSelectedCompany]);

  const hasCompany = !!company;

  // Build filters object for hooks
  const filters = useMemo(
    () => ({
      dateFrom: startDate || null,
      dateTo: endDate || null,
      company: company || undefined,
      offset,
      limit,
      // Pass through any sort/filter params
      ...Object.fromEntries(
        [...searchParams.entries()].filter(
          ([k]) =>
            k.startsWith('sort') ||
            ['projectName', 'employeeName', 'clientName'].includes(k)
        )
      ),
    }),
    [searchParams, startDate, endDate, company, offset, limit]
  );

  const { data: summaryData } = useReportSummary(
    hasCompany
      ? {
          dateFrom: startDate || undefined,
          dateTo: endDate || undefined,
          company,
        }
      : {}
  );
  const summary = summaryData?.data;

  const reportTypes = useMemo(() => {
    const types = [{ label: t('byProject'), value: 'by-project' }];
    if (isAdmin) {
      types.push({ label: t('byEmployee'), value: 'by-employee' });
      types.push({ label: t('byClient'), value: 'by-client' });
    }
    return types;
  }, [isAdmin, t]);

  // Ensure non-admin can't access restricted report types
  const activeReportType =
    !isAdmin && reportType !== 'by-project' ? 'by-project' : reportType;

  const updateParams = useCallback(
    (updates) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleDateChange = useCallback(
    (dates) => {
      updateParams({
        startDate: dates?.[0]?.format('YYYY-MM-DD') || undefined,
        endDate: dates?.[1]?.format('YYYY-MM-DD') || undefined,
        offset: '1',
      });
    },
    [updateParams]
  );

  const handleReportTypeChange = useCallback(
    (value) => {
      updateParams({ reportType: value, offset: '1' });
    },
    [updateParams]
  );

  const handlePaginationChange = useCallback(
    (page, pageSize) => {
      updateParams({ offset: String(page), limit: String(pageSize) });
    },
    [updateParams]
  );

  const handlePageSizeTableChange = useCallback(
    ({ current, pageSize }) => {
      updateParams({ offset: String(current), limit: String(pageSize) });
    },
    [updateParams]
  );

  const handleResetFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const [exportOpen, setExportOpen] = useState(false);

  const dateValue =
    startDate && endDate
      ? [dayjs(startDate, 'YYYY-MM-DD'), dayjs(endDate, 'YYYY-MM-DD')]
      : null;

  const hasActiveFilters = [...searchParams.keys()].some(
    (k) => !['reportType'].includes(k)
  );

  return (
    <>
      <PageHeader>
        <div className="flex w-full flex-wrap items-center gap-2 bg-white p-1">
          {!companyData?.isSingle && (
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('choose', { value: t('company') })}
              className="h-[38px] w-full min-w-[200px] max-w-[394px] rounded outline outline-accent"
              loading={companyIsLoading}
              options={companyData?.options || []}
              value={company || undefined}
              onChange={(value) =>
                updateParams({ company: value, offset: '1' })
              }
              disabled={companyIsLoading}
            />
          )}
          <RangePicker
            value={dateValue}
            onChange={handleDateChange}
            format="DD.MM.YYYY"
            allowClear
            className="min-w-[240px]"
            placeholder={[t('dateFrom'), t('dateTo')]}
          />
        </div>
        {summary && (
          <div className="flex flex-wrap items-center gap-2 p-1">
            <SummaryCard
              icon={<ClockCircleOutlined />}
              iconBg="bg-blue-50"
              iconColor="text-blue-500"
              label={t('totalTracked')}
              value={`${summary.totalTracked?.toFixed(1)} h`}
            />
            {isAdmin ? (
              <>
                <SummaryCard
                  icon={<DollarOutlined />}
                  iconBg="bg-green-50"
                  iconColor="text-green-500"
                  label={t('billable')}
                  value={`${summary.totalBillable?.toFixed(1)} h`}
                />
                <SummaryCard
                  icon={<StopOutlined />}
                  iconBg="bg-amber-50"
                  iconColor="text-amber-500"
                  label={t('nonBillable')}
                  value={`${summary.totalNonBillable?.toFixed(1)} h`}
                />
              </>
            ) : (
              <>
                <SummaryCard
                  icon={<FileTextOutlined />}
                  iconBg="bg-purple-50"
                  iconColor="text-purple-500"
                  label={t('entries')}
                  value={summary.entryCount}
                />
                <SummaryCard
                  icon={<ProjectOutlined />}
                  iconBg="bg-teal-50"
                  iconColor="text-teal-500"
                  label={t('projects')}
                  value={summary.byProject?.length || 0}
                />
              </>
            )}
          </div>
        )}
      </PageHeader>

      {!hasCompany ? (
        <div className="py-12 text-center text-gray-400">
          {t('selectCompany')}
        </div>
      ) : (
        <>
          {/* Table section */}
          <div className="p-4 pb-0">
            <ContentWrapper>
              <ContentHeader
                title={
                  activeReportType === 'by-project'
                    ? t('byProject')
                    : activeReportType === 'by-employee'
                      ? t('byEmployee')
                      : t('byClient')
                }
              >
                <div className="flex items-center gap-1 rounded-full bg-white/20 p-1">
                  {reportTypes.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleReportTypeChange(opt.value)}
                      className={cn(
                        'whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-all',
                        activeReportType === opt.value
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-white/80 hover:text-white'
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => setExportOpen(true)}
                  className="btn-default !h-8 !rounded-full !border-white/30 !bg-white/20 !px-4 !text-white hover:!bg-white/30"
                >
                  {t('exportExcel')}
                </Button>
                {hasActiveFilters && (
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleResetFilters}
                    className="btn-default !h-8 !rounded-full !border-white/30 !bg-white/20 !px-4 !text-white hover:!bg-white/30"
                  >
                    {t('resetFilters')}
                  </Button>
                )}

                <PageSizeDropdown
                  data={[]}
                  page={parseInt(offset)}
                  pageSize={parseInt(limit)}
                  handleTableChange={handlePageSizeTableChange}
                />
              </ContentHeader>
              <ContentBody>
                {activeReportType === 'by-project' && (
                  <ReportByProject
                    filters={filters}
                    isAdmin={isAdmin}
                    offset={parseInt(offset)}
                    limit={parseInt(limit)}
                    onPaginationChange={handlePaginationChange}
                    searchParams={searchParams}
                    updateParams={updateParams}
                  />
                )}
                {activeReportType === 'by-employee' && (
                  <ReportByEmployee
                    filters={filters}
                    offset={parseInt(offset)}
                    limit={parseInt(limit)}
                    onPaginationChange={handlePaginationChange}
                    searchParams={searchParams}
                    updateParams={updateParams}
                  />
                )}
                {activeReportType === 'by-client' && (
                  <ReportByClient
                    filters={filters}
                    offset={parseInt(offset)}
                    limit={parseInt(limit)}
                    onPaginationChange={handlePaginationChange}
                    searchParams={searchParams}
                    updateParams={updateParams}
                  />
                )}
              </ContentBody>
            </ContentWrapper>
          </div>

          {/* Analytics — works with or without date range */}
          <ReportAnalytics
            dateFrom={startDate || null}
            dateTo={endDate || null}
            company={company}
            isAdmin={isAdmin}
          />
        </>
      )}

      <ReportExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        company={company}
        isAdmin={isAdmin}
        defaultDateFrom={startDate}
        defaultDateTo={endDate}
      />
    </>
  );
}

function SummaryCard({ icon, iconBg, iconColor, label, value }) {
  return (
    <div className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 shadow-sm ring-1 ring-gray-100">
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          iconBg
        )}
      >
        <span className={cn('text-sm', iconColor)}>{icon}</span>
      </div>
      <div className="min-w-0">
        <div className="truncate text-[10px] font-medium text-gray-400">
          {label}
        </div>
        <div className="text-sm font-bold text-gray-900">{value}</div>
      </div>
    </div>
  );
}
